import { SETTLED_DONATION_STATUSES } from '@impact/types/enums';

// ─── Input ───

export interface PaymentRedirectContext {
  status: string;
  sessionData: string | null;
  sessionId: string | null;
}

// ─── Output (discriminated union) ───

export interface FinalizingSession {
  donationId: string;
  sessionId: string;
  sessionData: string;
  redirectResult: string;
}

export type PaymentRedirectResolution =
  | { kind: 'already-settled'; status: string }
  | { kind: 'finalize-payment'; session: FinalizingSession }
  | { kind: 'awaiting-settlement' }
  | { kind: 'failed'; status: string };

// ─── Resolution logic ───

/**
 * Generic, flow-agnostic redirect resolution.
 *
 * Determines the correct path after a user returns from a bank redirect
 * (iDEAL, Wero, Multibanco, etc.):
 *
 * 1. **already-settled** — webhook beat the browser; donation is paid/failed/cancelled.
 * 2. **finalize-payment** — donation still pending, session data available for SDK `submitDetails`.
 * 3. **awaiting-settlement** — donation still pending but no session data; consumer should poll.
 * 4. **failed** — donation reached a terminal non-paid state.
 */
export function resolvePaymentRedirectFlow(params: {
  donationId: string;
  redirectResult: string;
  sessionId: string | null;
  redirectContext: PaymentRedirectContext;
  storedSessionData: string | null;
}): PaymentRedirectResolution {
  const { donationId, redirectResult, redirectContext } = params;

  // ── PATH 1: Webhook already settled the donation ──
  if ((SETTLED_DONATION_STATUSES as readonly string[]).includes(redirectContext.status)) {
    return { kind: 'already-settled', status: redirectContext.status };
  }

  // ── PATH 2: Still pending — try to resolve session for SDK finalization ──
  const sessionData = params.storedSessionData || redirectContext.sessionData;
  const sessionId = params.sessionId || redirectContext.sessionId;

  if (sessionId && sessionData) {
    return {
      kind: 'finalize-payment',
      session: { donationId, sessionId, sessionData, redirectResult },
    };
  }

  // ── PATH 3: Pending but no session data — poll for webhook settlement ──
  // (Path 1 already returned for all settled statuses, so we're definitionally pending here)
  return { kind: 'awaiting-settlement' };
}
